<template>
  <v-sheet rounded elevation="1" class="cmd-bar px-4 py-3 mb-3">
    <!-- zone 1: navigation + actions -->
    <div class="d-flex align-center flex-wrap justify-space-between cmd-bar__nav" style="gap: 8px 12px">
      <div class="d-flex align-center flex-wrap cmd-bar__lead" style="gap: 8px 12px">
        <span v-if="total != null" class="cmd-bar__total">
          <v-icon x-small class="mr-1">mdi-calendar-check-outline</v-icon>{{ total }}
        </span>
        <v-btn-toggle v-if="view === 'list' || view === 'archive'" :value="scopeValue" mandatory dense
          class="cmd-bar__seg cmd-bar__scope">
          <v-btn small @click="onScope(true)">
            <v-icon small>mdi-play-circle-outline</v-icon>
            <span class="hidden-xs-only ml-1">{{ $t("custom.active_bookings") }}</span>
            <span class="hidden-sm-and-up ml-1">{{ scopeShort($t("custom.active_bookings")) }}</span>
          </v-btn>
          <v-btn small @click="onScope(false)">
            <v-icon small>mdi-history</v-icon>
            <span class="hidden-xs-only ml-1">{{ $t("custom.unactive_bookings") }}</span>
            <span class="hidden-sm-and-up ml-1">{{ scopeShort($t("custom.unactive_bookings")) }}</span>
          </v-btn>
          <v-btn small :to="{ name: 'ArchiveBookings' }">
            <v-icon small>mdi-archive-outline</v-icon>
            <span class="ml-1">{{ $t("custom.archive") }}</span>
          </v-btn>
        </v-btn-toggle>
      </div>

      <div class="d-flex align-center cmd-bar__actions" style="gap: 8px">
        <div class="d-flex align-center" style="gap: 8px">
          <span v-if="showLive" class="cmd-bar__dot hidden-xs-only" :class="isOnline ? 'is-on' : 'is-off'" v-tooltip="{
            content: isOnline ? $t('custom.connected') : $t('custom.disconnected'),
            placement: 'bottom-center', classes: ['info'], targetClasses: ['it-has-a-tooltip'],
            delay: { show: 300, hide: 300 },
          }"></span>
          <!-- в архиве переключатель вида неприменим — архив не список и не доска -->
          <v-btn-toggle v-if="view !== 'archive'" :value="view === 'list' ? 0 : 1" mandatory dense
            class="cmd-bar__seg">
            <v-btn small :to="{ name: 'BookingsList' }">
              <v-icon small>mdi-format-list-bulleted</v-icon>
              <span class="hidden-xs-only ml-1">{{ $t("custom.list_view") }}</span>
            </v-btn>
            <v-btn small :to="{ name: 'BookingsBoard' }">
              <v-icon small>mdi-view-column-outline</v-icon>
              <span class="hidden-xs-only ml-1">{{ $t("custom.board_view") }}</span>
            </v-btn>
          </v-btn-toggle>
        </div>

        <div class="d-flex align-center" style="gap: 8px">
          <v-btn v-if="selectedCount > 0" icon small color="red" @click="$emit('archive-selected')" v-tooltip="{
            content: $t('bookings.archivate_booking'),
            placement: 'bottom-center', classes: ['info'], targetClasses: ['it-has-a-tooltip'],
            delay: { show: 300, hide: 300 },
          }">
            <v-icon>mdi-archive-arrow-down</v-icon>
          </v-btn>

          <v-btn v-if="view !== 'archive'" small color="success" :to="{ name: 'NewBooking' }"
            :disabled="!isStaff && !isAgent">
            <v-icon small>mdi-plus</v-icon>
            <span class="ml-1">{{ $t("custom.add") }}</span>
          </v-btn>

          <v-menu offset-y left min-width="210">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon small v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <v-list-item :disabled="!canExport" @click="$emit('export-xlsx')">
              <v-list-item-icon class="mr-3"><v-icon :color="xlsxLoading ? 'primary' : ''">mdi-file-excel-outline</v-icon></v-list-item-icon>
              <v-list-item-title>{{ $t("custom.export_excel") }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="view !== 'archive'" :disabled="!canExport" @click="$emit('export-ical')">
              <v-list-item-icon class="mr-3"><v-icon :color="icalLoading ? 'primary' : ''">mdi-calendar-export</v-icon></v-list-item-icon>
              <v-list-item-title>{{ $t("tooltips.download_icalendar") }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$emit('refresh')">
              <v-list-item-icon class="mr-3"><v-icon>mdi-refresh</v-icon></v-list-item-icon>
              <v-list-item-title>{{ $t("custom.refresh") }}</v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item :href="helpHref" target="_blank">
              <v-list-item-icon class="mr-3"><v-icon>mdi-help-circle</v-icon></v-list-item-icon>
              <v-list-item-title>{{ $t("custom.help") }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        </div>
      </div>
    </div>

    <v-divider class="cmd-bar__rule my-3"></v-divider>

    <!-- zone 2: search + filters -->
    <div class="d-flex align-center flex-wrap" style="gap: 8px 12px">
      <v-text-field v-model="searchText" :label="$t('custom.search')" prepend-inner-icon="mdi-magnify"
        clearable hide-details dense outlined class="cmd-bar__search" :data-testid="searchTestid"
        @input="onSearchInput" @click:clear="onSearchClear" />

      <!-- в архиве панель фильтров списка неприменима -->
      <v-menu v-if="view !== 'archive'" :close-on-content-click="false" offset-y left
        transition="slide-y-transition" eager>
        <template v-slot:activator="{ on, attrs }">
          <v-btn small outlined class="cmd-bar__filters" v-bind="attrs" v-on="on">
            <v-icon left small>mdi-tune-variant</v-icon>{{ $t("crm.toolbar.filters") }}
            <span v-if="activeCount" class="cmd-bar__fbadge">{{ activeCount }}</span>
          </v-btn>
        </template>
        <bookings-filter-panel :value="filters" :cars="cars" :responsibles="responsibles" :states="states"
          :show-sort="showSort" :show-deposit="view === 'board'" :cars-loading="carsLoading"
          :responsibles-loading="responsiblesLoading"
          @input="$emit('input', $event)" @load-cars="$emit('load-cars')"
          @load-responsibles="$emit('load-responsibles')" />
      </v-menu>
    </div>

    <!-- zone 3: active filter chips -->
    <div v-if="activeChips.length" class="d-flex align-center flex-wrap mt-3" style="gap: 8px">
      <v-chip v-for="chip in activeChips" :key="chip.key" small close class="cmd-bar__chip"
        @click:close="removeChip(chip.key)">
        <span class="cmd-bar__chipk">{{ chip.label }}:</span>&nbsp;{{ chip.text }}
      </v-chip>
      <v-btn small text class="cmd-bar__clear" @click="$emit('reset')">
        <v-icon left small>mdi-filter-off-outline</v-icon>{{ $t("custom.clear_all") }}
      </v-btn>
    </div>
  </v-sheet>
</template>

<script>
import BookingsFilterPanel from "@/components/dashboard/bookings/BookingsFilterPanel.vue";

export default {
  name: "BookingsCommandBar",
  components: { BookingsFilterPanel },
  props: {
    view: { type: String, default: "list" },
    total: { type: [Number, String], default: null },
    scope: { type: String, default: "active" },
    filters: { type: Object, required: true },
    cars: { type: Array, default: () => [] },
    responsibles: { type: Array, default: () => [] },
    states: { type: Array, default: () => [] },
    carsLoading: { type: Boolean, default: false },
    responsiblesLoading: { type: Boolean, default: false },
    connected: { type: [String, Boolean], default: false },
    showSort: { type: Boolean, default: false },
    xlsxLoading: { type: Boolean, default: false },
    icalLoading: { type: Boolean, default: false },
    selectedCount: { type: Number, default: 0 },
    showLive: { type: Boolean, default: true },
    // e2e-селектор поля поиска: у архива свой (archive-bookings-search)
    searchTestid: { type: String, default: "bookings-search" },
  },
  data() {
    return {
      searchText: (this.filters && this.filters.search) || "",
      searchTimer: null,
    };
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  computed: {
    // подсветка скоуп-сегмента: в архиве — «Архив» (2), в списке — по scope
    scopeValue() {
      return this.view === "archive" ? 2 : this.scope === "active" ? 0 : 1;
    },
    isOnline() {
      return this.connected === true || this.connected === "success";
    },
    canExport() {
      // Возврат к состоянию master: выгрузка реестра броней (с ФИО клиентов)
      // — для admin+. В коммите f6ea8aef я расширил гейт до isStaff под
      // предлогом «гейт был строже обработчика», но гейт строже обработчика
      // — это нормальное наслоение, а не дефект. Дефект — обратное
      // (гейт слабее обработчика → тупик). Расширение прав в
      // рефакторной ветке было ошибкой.
      return this.isAdminPlus;
    },
    helpHref() {
      const locale = this.$root.$i18n.locale;
      return `https://rentprog.${locale === "ru" ? "ru" : "com"}/${locale}/categories/2`;
    },
    activeChips() {
      const f = this.filters || {};
      const chips = [];
      if (f.state && f.state.length) {
        chips.push({ key: "state", label: this.$t("board.state_label"), text: f.state.join(", ") });
      }
      if (f.car_id) {
        chips.push({ key: "car_id", label: this.$t("board.vehicle"), text: this.carName(f.car_id) });
      }
      if (f.responsible) {
        chips.push({ key: "responsible", label: this.$t("board.responsible"), text: f.responsible });
      }
      if (f.date_from || f.date_to) {
        chips.push({ key: "dates", label: this.$t("custom.date"), text: `${this.fmtDate(f.date_from)} – ${this.fmtDate(f.date_to)}` });
      }
      if (f.payment_status) {
        chips.push({ key: "payment_status", label: this.$t("board.payment_status"), text: this.paymentStatusText(f.payment_status) });
      }
      if (f.overdue) {
        chips.push({ key: "overdue", label: this.$t("board.overdue_label"), text: this.$t("custom.yes") });
      }
      if (f.deposit_return) {
        chips.push({ key: "deposit_return", label: this.$t("board.deposit_return_label"), text: this.$t("custom.yes") });
      }
      return chips;
    },
    activeCount() {
      return this.activeChips.length;
    },
  },
  watch: {
    "filters.search"(v) {
      if ((v || "") !== this.searchText) this.searchText = v || "";
    },
  },
  methods: {
    // из списка — эмит скоупа странице; из архива — навигация в список с нужным скоупом
    onScope(active) {
      if (this.view === "archive") {
        this.$router.push({ name: "BookingsList", query: { scope: active ? "active" : "inactive" } });
      } else {
        this.$emit("set-scope", active);
      }
    },
    // mobile scope label: drop the trailing word ("Активные брони" -> "Активные")
    scopeShort(text) {
      const parts = (text || "").split(" ");
      return parts.length > 1 ? parts.slice(0, -1).join(" ") : text;
    },
    // "YYYY-MM-DD" -> "DD.MM.YYYY" (dates come only from the picker/presets)
    fmtDate(d) {
      if (!d) return "…";
      const p = String(d).split("-");
      return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : d;
    },
    carName(id) {
      const c = (this.cars || []).find((x) => x.id === id);
      if (!c) return id;
      return `${c.car_name || ""}${c.code ? " (" + c.code + ")" : ""}`.trim();
    },
    paymentStatusText(v) {
      const key = { underpaid: "board.pay_underpaid", paid: "board.pay_paid", overpaid: "board.pay_overpaid" }[v];
      return key ? this.$t(key) : v;
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        const q = (this.searchText || "").trim();
        // need at least 2 chars to search; fewer means "not searching" (show all)
        const next = q.length >= 2 ? q : null;
        // skip when nothing effectively changed (e.g. typing the 1st char)
        if ((this.filters.search || null) === next) return;
        this.$emit("input", { ...this.filters, search: next });
      }, 300);
    },
    onSearchClear() {
      this.searchText = "";
      this.$emit("input", { ...this.filters, search: null });
    },
    removeChip(key) {
      const next = { ...this.filters };
      if (key === "state") next.state = [];
      else if (key === "dates") { next.date_from = null; next.date_to = null; }
      else next[key] = null;
      this.$emit("input", next);
    },
  },
};
</script>

<style scoped>
.cmd-bar__total {
  font-size: 0.85rem;
  font-weight: 600;
  opacity: 0.6;
  display: inline-flex;
  align-items: center;
}
/* segmented controls: outlined group so the buttons read as one unit */
.cmd-bar__seg {
  background: rgba(127, 127, 127, 0.08);
  border: 1px solid rgba(127, 127, 127, 0.35);
  border-radius: 10px;
  padding: 2px;
}
.cmd-bar__seg >>> .v-btn {
  text-transform: none;
  letter-spacing: 0;
  border: none !important;
  border-radius: 8px !important;
  margin: 0 !important;
  background: transparent !important;
}
.cmd-bar__seg >>> .v-btn.v-btn--active {
  background: rgba(127, 127, 127, 0.3) !important;
}
.cmd-bar__rule {
  opacity: 0.6;
}
.cmd-bar__search {
  flex: 2 1 260px;
  min-width: 200px;
}
.cmd-bar__filters {
  text-transform: none;
  letter-spacing: 0;
  height: 40px !important;
}
.cmd-bar__fbadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  margin-left: 7px;
  border-radius: 9px;
  font-size: 11px;
  font-weight: 600;
  background: var(--v-success-base, #4caf50);
  color: #fff;
}
.cmd-bar__chip {
  font-size: 12px;
}
.cmd-bar__chipk {
  opacity: 0.6;
}
.cmd-bar__clear {
  text-transform: none;
  letter-spacing: 0;
}
/* ActionCable live indicator */
.cmd-bar__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  margin: 0 4px;
  flex: none;
}
.cmd-bar__dot.is-on {
  background: #4caf50;
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.18);
}
.cmd-bar__dot.is-off {
  background: #e53935;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.18);
}
/* Mobile: scope + actions each span a full row; search shares its row with Filters. */
@media (max-width: 599px) {
  .cmd-bar__lead {
    width: 100%;
  }
  .cmd-bar__scope {
    width: 100%;
  }
  .cmd-bar__scope >>> .v-btn {
    flex: 1 1 0;
    min-width: 0;
    padding: 0 6px !important;
    font-size: 11px;
  }
  .cmd-bar__actions {
    width: 100%;
    justify-content: space-between;
  }
  .cmd-bar__search {
    flex: 1 1 140px;
    min-width: 0;
  }
}
</style>
